import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag, Truck } from "lucide-react";
import { useStore, formatINR } from "@/store/StoreContext";
import { imgW, imgFallback } from "@/utils/img";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateCartItem, removeCartItem, settings } = useStore();
  const navigate = useNavigate();
  if (!cartOpen) return null;
  const items = cart.items || [];
  const totals = cart.totals;
  const threshold = settings?.shipping?.free_threshold || 1999;
  const subtotal = totals?.subtotal || 0;
  const progress = Math.min(100, (subtotal / threshold) * 100);
  const away = Math.max(0, threshold - subtotal);
  const offers = settings?.prepaid_offers || [];
  const qualified = offers.filter((o) => subtotal >= (o.min_order || 0)).sort((a, b) => (b.min_order || 0) - (a.min_order || 0))[0];
  const upcoming = !qualified && offers.length ? [...offers].sort((a, b) => (a.min_order || 0) - (b.min_order || 0))[0] : null;
  const offer = qualified || upcoming;
  const offerDesc = (o) => [
    o.discount_value > 0 ? (o.discount_type === "percent" ? `${o.discount_value}% extra off` : `₹${o.discount_value} extra off`) : null,
    o.shipping_discount_type === "free" ? "FREE shipping" : null,
    o.shipping_discount_type === "percent" && o.shipping_discount_value > 0 ? `${o.shipping_discount_value}% off shipping` : null,
    o.shipping_discount_type === "flat" && o.shipping_discount_value > 0 ? `₹${o.shipping_discount_value} off shipping` : null,
  ].filter(Boolean).join(" + ");

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-[#111111]/60 backdrop-blur-sm" onClick={() => setCartOpen(false)} />
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#FFFFFF] flex flex-col shadow-2xl" data-testid="cart-drawer">
        <div className="flex items-center justify-between p-5 border-b border-[#EAEAEA]">
          <h2 className="font-serif text-2xl">Your Bag <span className="text-sm text-[#C59B27]">({items.length})</span></h2>
          <button onClick={() => setCartOpen(false)} aria-label="Close cart" data-testid="cart-drawer-close"><X size={22} /></button>
        </div>
        {items.length > 0 && (
          <div className="px-5 py-3 bg-[#FAFAFA] border-b border-[#EAEAEA]">
            {away > 0 ? (
              <p className="text-xs text-[#666666] flex items-center gap-2"><Truck size={14} /> You're <strong>{formatINR(away)}</strong> away from FREE shipping</p>
            ) : (
              <p className="text-xs text-[#1A3C34] font-semibold flex items-center gap-2"><Truck size={14} /> You've unlocked FREE shipping</p>
            )}
            <div className="mt-2 h-1 bg-[#EAEAEA] rounded-full overflow-hidden">
              <div className="h-full bg-[#C59B27] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            {offer && (
              <p className="text-[11px] text-[#8A6A12] mt-2" data-testid="prepaid-teaser">
                {qualified
                  ? <>Pay online & get <strong>{offerDesc(qualified)}</strong></>
                  : <>Add <strong>{formatINR(Math.max(0, (upcoming.min_order || 0) - subtotal))}</strong> more & pay online to get <strong>{offerDesc(upcoming)}</strong></>}
              </p>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-5">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center" data-testid="cart-empty-state">
              <ShoppingBag size={44} className="text-[#A8821B] mb-4" strokeWidth={1.2} />
              <p className="font-serif text-2xl mb-2">Your bag is empty</p>
              <p className="text-sm text-[#666666] mb-6">Beautiful pairs are waiting for you.</p>
              <Link to="/collections/new-arrivals" onClick={() => setCartOpen(false)} className="tq-btn-dark">Shop New Arrivals</Link>
            </div>
          ) : (
            <ul className="space-y-5">
              {items.map((it) => (
                <li key={it.variant_id} className="flex gap-4" data-testid={`cart-item-${it.sku}`}>
                  <Link to={`/products/${it.slug}`} onClick={() => setCartOpen(false)} className="shrink-0">
                    <img src={imgW(it.image, 200)} alt={it.name} loading="lazy" onError={imgFallback} className="w-20 h-24 object-cover bg-[#FAFAFA]" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${it.slug}`} onClick={() => setCartOpen(false)}>
                      <p className="font-serif text-base leading-snug line-clamp-2">{it.name}</p>
                    </Link>
                    <p className="text-xs text-[#C59B27] mt-1">Size {it.size}{it.color ? ` · ${it.color}` : ""}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-[#EAEAEA]">
                        <button onClick={() => updateCartItem(it.variant_id, it.qty - 1)} aria-label="Decrease quantity"
                          data-testid={`cart-qty-minus-${it.sku}`} className="w-8 h-8 flex items-center justify-center hover:bg-[#FAFAFA]"><Minus size={13} /></button>
                        <span className="w-8 text-center text-sm">{it.qty}</span>
                        <button onClick={() => updateCartItem(it.variant_id, it.qty + 1)} aria-label="Increase quantity"
                          data-testid={`cart-qty-plus-${it.sku}`} className="w-8 h-8 flex items-center justify-center hover:bg-[#FAFAFA]"><Plus size={13} /></button>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">{formatINR(it.price * it.qty)}</p>
                        {it.mrp > it.price && <p className="text-[11px] text-[#8E8E93] line-through">{formatINR(it.mrp * it.qty)}</p>}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => removeCartItem(it.variant_id)} aria-label="Remove item"
                    data-testid={`cart-remove-${it.sku}`} className="self-start p-1 text-[#8E8E93] hover:text-[#8B1E21] transition-colors">
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {items.length > 0 && totals && (
          <div className="border-t border-[#EAEAEA] p-5 bg-white">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#666666]">Subtotal</span><span className="font-semibold">{formatINR(totals.subtotal)}</span>
            </div>
            {totals.savings > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#1A3C34]">Total savings</span><span className="text-[#1A3C34] font-semibold">−{formatINR(totals.savings)}</span>
              </div>
            )}
            <p className="text-[11px] text-[#8E8E93] mb-4">Shipping & taxes calculated at checkout</p>
            <button onClick={() => { setCartOpen(false); navigate("/checkout"); }} data-testid="cart-drawer-checkout-btn" className="tq-btn-dark w-full">
              Proceed to Checkout
            </button>
            <button onClick={() => { setCartOpen(false); navigate("/cart"); }} data-testid="cart-view-full-btn" className="tq-btn-outline w-full mt-2">
              View Bag
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
